(() => {
  if (window.__tanSizeGuideAssetReady) {
    window.dispatchEvent(new CustomEvent('tan:size-guide:init'));
    return;
  }
  window.__tanSizeGuideAssetReady = true;

  const clean = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const readCopy = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-tan-size-guide-copy]')?.textContent || '{}');
    } catch (_) {
      return {};
    }
  };
  const interpolate = (template, values = {}) => Object.entries(values).reduce(
    (output, [key, value]) => output.split(`{${key}}`).join(String(value)),
    String(template || ''),
  );
  const number = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const sizeBand = (value) => {
    if (value < 80) return 'under_80';
    if (value <= 100) return '80_100';
    if (value <= 150) return '120_150';
    if (value <= 200) return '151_200';
    return 'over_200';
  };
  const publish = (name, details) => {
    const data = { event_version: '1.0', page_type: 'size_guide', ...details };
    window.dispatchEvent(new CustomEvent('tan:lead-event', { detail: { name, data } }));
    try {
      window.Shopify?.analytics?.publish?.('the_art_neon:' + name, data);
    } catch (_) {
      // Every planning control remains usable when analytics is unavailable.
    }
  };
  const availableSizes = (value) => {
    const parsed = clean(value).split(',').map((item) => number(item)).filter((item) => item >= 20 && item <= 500);
    return [...new Set(parsed)].sort((a, b) => a - b);
  };
  const nearestIndex = (values, target) => {
    let selected = 0;
    let distance = Infinity;
    values.forEach((value, index) => {
      const nextDistance = Math.abs(value - target);
      if (nextDistance < distance) {
        selected = index;
        distance = nextDistance;
      }
    });
    return selected;
  };

  const initializeFinder = (root) => {
    if (!root || root.dataset.sizeFinderReady) return;
    root.dataset.sizeFinderReady = 'true';
    const copy = readCopy(root);
    const measurement = (value) => interpolate(copy.format?.measurementCm, { value });
    const range = (low, high) => interpolate(copy.format?.rangeCm, { low, high });
    const rangePlus = (low, limit) => interpolate(copy.format?.rangePlusCm, { low, limit });

    const values = availableSizes(root.dataset.sizeValues);
    if (!values.length) values.push(50, 60, 70, 80, 90, 100, 120, 150, 200);
    const choices = Array.from(root.querySelectorAll('[data-size-choice]'));
    const wall = root.querySelector('[data-size-wall]');
    const characters = root.querySelector('[data-size-characters]');
    const font = root.querySelector('[data-size-font]');
    const environment = root.querySelector('[data-size-environment]');
    const rangeOutput = root.querySelector('[data-size-range]');
    const balancedOutput = root.querySelector('[data-size-balanced]');
    const impactOutput = root.querySelector('[data-size-impact]');
    const explanation = root.querySelector('[data-size-explanation]');
    const caveatsOutput = root.querySelector('[data-size-caveats]');
    const meter = root.querySelector('[data-size-meter]');
    const primary = root.querySelector('[data-size-primary]');
    const copyButton = root.querySelector('[data-size-copy]');
    const briefOutput = root.querySelector('[data-tan-brief-output]');

    const active = (group) => root.querySelector('[data-size-choice][data-choice-group="' + group + '"].is-active');
    const selectedLabel = (select) => clean(select?.selectedOptions?.[0]?.textContent);
    const state = () => ({
      useCase: active('size_use'),
      distance: active('size_distance'),
      design: active('size_design'),
      wallWidth: clamp(number(wall?.value, number(root.dataset.defaultWall, 240)), 80, 800),
      characters: clean(characters?.value),
      font: clean(font?.value),
      environment: clean(environment?.value),
    });

    const calculate = () => {
      const current = state();
      if (!current.useCase || !current.distance || !current.design) return null;

      const coverage = clamp(number(current.useCase.dataset.caseCoverage, 38) / 100, .2, .8);
      const useMinimum = number(current.useCase.dataset.caseMin, values[0]);
      const distanceAddition = number(current.distance.dataset.distanceAdd);
      const designAddition = number(current.design.dataset.designAdd);
      const characterAddition = current.characters === '11_18' ? 10 : current.characters === '19_plus' ? 30 : 0;
      const fontAddition = current.font === 'script' ? 10 : current.font === 'outline' ? 20 : 0;
      const environmentAddition = current.environment === 'window' ? 10 : current.environment === 'outdoor' ? 20 : 0;
      const unconstrained = Math.max(useMinimum, current.wallWidth * coverage)
        + distanceAddition + designAddition + characterAddition + fontAddition + environmentAddition;
      const wallLimit = Math.max(values[0], current.wallWidth * clamp(number(root.dataset.maxCoverage, 72) / 100, .5, .9));
      const target = Math.min(unconstrained, wallLimit);
      const index = nearestIndex(values, target);
      const balanced = values[index];
      const low = values[Math.max(0, index - 1)];
      const high = values[Math.min(values.length - 1, index + 1)];
      const exceedsScale = unconstrained > values[values.length - 1];
      const constrained = unconstrained > wallLimit + 5;
      const route = clean(current.design.dataset.route) || 'quote';
      const tailored = route !== 'text'
        || current.distance.dataset.choiceId === 'long'
        || current.characters === '19_plus'
        || current.environment === 'outdoor'
        || exceedsScale;
      return { ...current, balanced, low, high, index, unconstrained, exceedsScale, constrained, tailored, route };
    };

    const updateBrief = (result) => {
      if (!briefOutput || !result) return;
      const comparedRange = result.exceedsScale ? rangePlus(result.low, 200) : range(result.low, result.high);
      const comparison = result.exceedsScale || result.index === values.length - 1
        ? interpolate(copy.format?.tailoredReviewCm, { limit: 200 })
        : measurement(result.high);
      briefOutput.value = [
        copy.brief?.title,
        interpolate(copy.brief?.range, { value: comparedRange }),
        interpolate(copy.brief?.balanced, { value: measurement(result.balanced) }),
        interpolate(copy.brief?.comparison, { value: comparison }),
        interpolate(copy.brief?.useCase, { value: clean(result.useCase.dataset.choiceTitle) }),
        interpolate(copy.brief?.distance, { value: clean(result.distance.dataset.choiceTitle) }),
        interpolate(copy.brief?.design, { value: clean(result.design.dataset.choiceTitle) }),
        interpolate(copy.brief?.wallWidth, { value: measurement(result.wallWidth) }),
        interpolate(copy.brief?.textLength, { value: selectedLabel(characters) }),
        interpolate(copy.brief?.font, { value: selectedLabel(font) }),
        interpolate(copy.brief?.environment, { value: selectedLabel(environment) }),
        '',
        copy.brief?.boundary,
      ].join('\n');
    };

    const update = () => {
      const result = calculate();
      if (!result) return;
      const rangeText = result.exceedsScale ? rangePlus(result.low, 200) : range(result.low, result.high);
      const impactText = result.exceedsScale || result.index === values.length - 1
        ? interpolate(copy.format?.reviewCm, { limit: 200 })
        : measurement(result.high);
      if (rangeOutput) rangeOutput.textContent = rangeText;
      if (balancedOutput) balancedOutput.textContent = measurement(result.balanced);
      if (impactOutput) impactOutput.textContent = impactText;
      if (meter) meter.style.width = clamp((result.balanced / values[values.length - 1]) * 100, 8, 100) + '%';

      const guidance = [result.useCase, result.distance, result.design]
        .map((item) => clean(item.dataset.guidance))
        .filter(Boolean);
      if (explanation) explanation.textContent = guidance.join(' ');

      const caveats = [
        copy.caveats?.dimensions,
        result.constrained ? copy.caveats?.constrained : '',
        result.characters === '19_plus' ? copy.caveats?.longWording : '',
        result.font === 'outline' ? copy.caveats?.outline : '',
        result.environment === 'window' ? copy.caveats?.window : '',
        result.environment === 'outdoor' ? copy.caveats?.outdoor : '',
        result.exceedsScale ? copy.caveats?.scale : '',
        copy.caveats?.final,
      ].filter(Boolean);
      if (caveatsOutput) {
        caveatsOutput.replaceChildren(...caveats.map((text) => {
          const item = document.createElement('li');
          item.textContent = text;
          return item;
        }));
      }

      if (primary) {
        const baseLink = primary.dataset.baseLink || clean(result.design.dataset.ctaLink);
        primary.dataset.baseLink = baseLink;
        primary.textContent = clean(result.design.dataset.ctaLabel) || (result.tailored ? copy.cta?.requestReview : copy.cta?.continue);
        primary.hidden = !baseLink;
        if (baseLink) {
          try {
            const url = new URL(baseLink, window.location.href);
            url.searchParams.set('tan_source', 'size_guide');
            url.searchParams.set('tan_size_cm', result.exceedsScale ? '200_plus' : String(result.balanced));
            url.searchParams.set('tan_size_range', result.exceedsScale ? result.low + '_200_plus' : result.low + '_' + result.high);
            primary.href = url.toString();
          } catch (_) {
            primary.href = baseLink;
          }
        }
      }

      updateBrief(result);
      window.dispatchEvent(new CustomEvent('tan:size-recommendation', {
        detail: { balanced: result.balanced, low: result.low, high: result.high, wallWidth: result.wallWidth },
      }));
      root.__tanSizeResult = result;
    };

    choices.forEach((choice) => {
      choice.addEventListener('click', () => {
        choices
          .filter((candidate) => candidate.dataset.choiceGroup === choice.dataset.choiceGroup)
          .forEach((candidate) => {
            const selected = candidate === choice;
            candidate.classList.toggle('is-active', selected);
            candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
        if (primary) primary.dataset.baseLink = clean(choice.dataset.choiceGroup === 'size_design' ? choice.dataset.ctaLink : primary.dataset.baseLink);
        update();
      });
    });
    [wall, characters, font, environment].filter(Boolean).forEach((field) => {
      field.addEventListener('input', update);
      field.addEventListener('change', update);
    });

    primary?.addEventListener('click', () => {
      const result = root.__tanSizeResult || calculate();
      if (!result) return;
      publish('size_result_next_step', {
        route: result.route,
        use_case: clean(result.useCase.dataset.choiceId),
        distance_band: clean(result.distance.dataset.choiceId),
        design_type: clean(result.design.dataset.choiceId),
        text_length_band: result.characters,
        font_structure: result.font,
        environment: result.environment,
        size_band: sizeBand(result.exceedsScale ? 201 : result.balanced),
        has_wall_width: Boolean(wall?.value),
      });
    });

    copyButton?.addEventListener('click', async () => {
      update();
      const text = briefOutput?.value || '';
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        const original = copyButton.textContent;
        copyButton.textContent = copyButton.dataset.copiedLabel || original;
        window.setTimeout(() => { copyButton.textContent = original; }, 1800);
      } catch (_) {
        briefOutput.hidden = false;
        briefOutput.removeAttribute('aria-hidden');
        briefOutput.focus();
        briefOutput.select();
      }
    });

    update();
  };

  const initializeSimulator = (root) => {
    if (!root || root.dataset.sizeSimulatorReady) return;
    root.dataset.sizeSimulatorReady = 'true';
    const copy = readCopy(root);
    const measurement = (value) => interpolate(copy.format?.measurementCm, { value });
    const values = availableSizes(root.dataset.sizeValues);
    if (!values.length) values.push(50, 60, 70, 80, 90, 100, 120, 150, 200);

    const scenes = Array.from(root.querySelectorAll('[data-size-scene]'));
    const sizeInput = root.querySelector('[data-sim-size]');
    const wallInput = root.querySelector('[data-sim-wall]');
    const textInput = root.querySelector('[data-sim-text]');
    const ratioInput = root.querySelector('[data-sim-ratio]');
    const sign = root.querySelector('[data-sim-sign]');
    const signText = root.querySelector('[data-sim-sign-text]');
    const sizeOutputs = Array.from(root.querySelectorAll('[data-sim-size-output]'));
    const wallOutput = root.querySelector('[data-sim-wall-output]');
    const coverageOutput = root.querySelector('[data-sim-coverage-output]');
    const sceneOutput = root.querySelector('[data-sim-scene-output]');
    const note = root.querySelector('[data-sim-note]');
    let published = false;

    if (sizeInput) {
      sizeInput.min = '0';
      sizeInput.max = String(values.length - 1);
      sizeInput.step = '1';
      sizeInput.value = String(nearestIndex(values, number(root.dataset.defaultSize, 100)));
    }

    const activeScene = () => scenes.find((scene) => scene.classList.contains('is-active')) || scenes[0];
    const update = (isInteraction = false) => {
      const scene = activeScene();
      const size = values[clamp(number(sizeInput?.value), 0, values.length - 1)] || values[0];
      const wallWidth = clamp(number(wallInput?.value, number(scene?.dataset.wall, 300)), 100, 900);
      const coverage = clamp((size / wallWidth) * 100, 8, 94);
      const ratio = clamp(number(ratioInput?.value, 2.5), 1, 4);
      const previewText = clean(textInput?.value).slice(0, 22) || clean(root.dataset.defaultText) || clean(signText?.textContent);
      if (sign) {
        sign.style.setProperty('--sim-sign-width', coverage + '%');
        sign.style.setProperty('--sim-sign-ratio', String(ratio));
      }
      if (signText) signText.textContent = previewText;
      sizeOutputs.forEach((output) => { output.textContent = measurement(size); });
      if (wallOutput) wallOutput.textContent = measurement(wallWidth);
      if (coverageOutput) coverageOutput.textContent = interpolate(copy.format?.percent, { value: Math.round(coverage) });
      if (sceneOutput) sceneOutput.textContent = clean(scene?.dataset.sceneLabel);
      if (note) {
        note.textContent = coverage > 72
          ? clean(root.dataset.tightNote)
          : coverage < 25
            ? clean(root.dataset.smallNote)
            : clean(root.dataset.balancedNote);
      }
      if (isInteraction && !published) {
        published = true;
        publish('size_simulator_used', {
          scene: clean(scene?.dataset.sceneKey),
          size_band: sizeBand(size),
          wall_band: wallWidth < 200 ? 'under_200' : wallWidth <= 350 ? '200_350' : wallWidth <= 550 ? '351_550' : 'over_550',
        });
      }
    };

    scenes.forEach((scene) => {
      scene.addEventListener('click', () => {
        scenes.forEach((candidate) => {
          const selected = candidate === scene;
          candidate.classList.toggle('is-active', selected);
          candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        if (wallInput) wallInput.value = scene.dataset.wall || wallInput.value;
        root.style.setProperty('--sim-accent', scene.dataset.accent || '#ec4899');
        update(true);
      });
    });
    [sizeInput, wallInput, textInput, ratioInput].filter(Boolean).forEach((field) => {
      field.addEventListener('input', () => update(true));
      field.addEventListener('change', () => update(true));
    });
    window.addEventListener('tan:size-recommendation', (event) => {
      if (!event.detail) return;
      if (sizeInput) sizeInput.value = String(nearestIndex(values, number(event.detail.balanced, 100)));
      if (wallInput) wallInput.value = String(clamp(number(event.detail.wallWidth, 240), 100, 900));
      update();
    });
    update();
  };

  const initialize = (scope = document) => {
    if (scope.matches?.('[data-tan-size-simulator]')) initializeSimulator(scope);
    if (scope.matches?.('[data-tan-size-finder]')) initializeFinder(scope);
    scope.querySelectorAll?.('[data-tan-size-simulator]').forEach(initializeSimulator);
    scope.querySelectorAll?.('[data-tan-size-finder]').forEach(initializeFinder);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('tan:size-guide:init', () => initialize());
})();
